import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import WhitelistRequest from "./WhitelistRequest";
import CbdcRequest from "./CbdcRequest";
import GiniRequest from "./GiniRequest";
import Payment from "./Payment";

const RequestComponent = ({ address, isConnected }) => {
  const [showDiv, setShowDiv] = useState(false); // To show the request list
  const [isWhitelistRequestOpen, setIsWhitelistRequestOpen] = useState(false); // To show the whitelist form
  const [isCBDCRequestOpen, setIsCBDCRequestOpen] = useState(false);
  const [isGiniRequestOpen, setIsGiniRequestOpen] = useState(false);
  const [isRecharge, setIsRecharge] = useState(false);

  const [isRequestAccessCompleted, setIsRequestAccessCompleted] =
    useState(false); // Track Request Access completion

  const handleGetStarted = () => {
    setShowDiv(true); // Show the request list when "Get Started" is clicked
    // setIsRequestAccessCompleted(true); // Mark the Request Access step as completed
  };

  const handleWhitelistRequestClick = () => {
    setIsWhitelistRequestOpen(true); // Show the whitelist form
  };

  const handleBackClick = () => {
    setIsWhitelistRequestOpen(false); // Go back to the request list screen
  };

  const handleCbdcBackClick = () => {
    setIsCBDCRequestOpen(false);
  };

  const handleCbdcRequest = () => {
    setIsCBDCRequestOpen(true);
  };

  const handleGiniBackClick = () => {
    setIsGiniRequestOpen(false);
  };

  const handleGiniRequest = () => {
    setIsGiniRequestOpen(true);
  };

  // Render the WhitelistRequest if it's open
  if (isWhitelistRequestOpen) {
    return (
      <WhitelistRequest
        onBackClick={handleBackClick}
        merchantID={address}
        isRequestAccessCompleted={isRequestAccessCompleted}
        setIsRequestAccessCompleted={setIsRequestAccessCompleted}
      />
    );
  }

  if (isCBDCRequestOpen) {
    return (
      <CbdcRequest onBackClick={handleCbdcBackClick} merchantId={address} />
    );
  }
  if (isGiniRequestOpen) {
    return <GiniRequest goBack={handleGiniBackClick} merchantId={address} />;
  }
  if (isRecharge) {
    return <Payment goBack={()=>setIsRecharge(false)} merchantId={address} />;
  }

  // Main RequestComponent rendering
  return (
    <Box sx={{ padding: "20px", textAlign: "center" }}>
      {/* Steps Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "40px",
        }}
      >
        {/* Step 1 */}
        <Box sx={{ display: "flex", gap: "10px" }}>
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                backgroundColor: isConnected ? "#1A2E88" : "#fff",
                border: isConnected ? "none" : "2px solid #1A2E88",
                color: isConnected ? "#fff" : "#1A2E88",
                width: "50px",
                height: "30px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "14px",
                marginRight: "10px",
                marginTop: "-5px",
              }}
            >
              1
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: isRequestAccessCompleted ? "#1A2E88" : "#000",
                  }}
                >
                  Authentication
                </Typography>
                {/* Optional Subtext */}
              </Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#667085",
                  textAlign: "left",
                  textWrap: "wrap",
                }}
              >
                Visit web portal to complete your authentication process.
              </Typography>
            </Box>
          </Box>

          {/* Connector Line */}
          <Box
            sx={{
              width: "60%",
              height: "2px",
              backgroundColor: "#1A2E88",
              // marginRight: "40px",
              marginTop: "10px",
            }}
          />

          {/* Step 2 */}
          <Box sx={{ display: "flex", gap: "10px" }}>
            <Box
              sx={{
                backgroundColor: isRequestAccessCompleted ? "#1A2E88" : "#fff",
                border: isRequestAccessCompleted ? "none" : "2px solid #1A2E88",
                color: isRequestAccessCompleted ? "#fff" : "#1A2E88",
                width: "50px",
                height: "30px",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                fontWeight: "bold",
                fontSize: "14px",
                // marginRight: "10px",
              }}
            >
              2
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "14px",
                    color: isRequestAccessCompleted ? "#1A2E88" : "#000",
                  }}
                >
                  Request Access
                </Typography>
                {/* Optional Subtext */}
              </Box>
              <Typography
                sx={{
                  fontSize: "13px",
                  color: "#667085",
                  textAlign: "left",
                  textWrap: "wrap",
                }}
              >
                Establish a secure session & send access request
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Get Started Button */}
      <Button
        variant="contained"
        disabled={!isConnected}
        onClick={handleGetStarted}
        sx={{
          backgroundColor: "#1A2E88",
          color: "#fff",
          width: "100%",
          padding: "10px",
          borderRadius: "8px",
          fontWeight: "bold",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#162670",
          },
        }}
      >
        Get Started
      </Button>

      {/* Request List */}
      {showDiv && (
        <Box
          sx={{
            marginTop: "30px",
            padding: "20px",
            backgroundColor: "#f2f4f6",
            borderRadius: "8px",
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", marginBottom: "20px", textAlign: "left" }}
          >
            Request access
          </Typography>

          <List>
            {/* Whitelisting */}
            <ListItem
              button
              onClick={handleWhitelistRequestClick}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                marginBottom: "10px",
                boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              }}
            >
              <ListItemText primary="Whitelisting Request" />
              <ArrowForwardIosIcon fontSize="small" />
            </ListItem>

            {/* CBDC Token */}
            <ListItem
              button
              onClick={handleCbdcRequest}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                marginBottom: "10px",
                boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              }}
            >
              <ListItemText primary="CBDC Token Request" />
              <ArrowForwardIosIcon fontSize="small" />
            </ListItem>

            {/* Gini Token */}
            <ListItem
              button
              onClick={handleGiniRequest}
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                marginBottom: "10px",
                boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              }}
            >
              <ListItemText primary="Gini Token Request" />
              <ArrowForwardIosIcon fontSize="small" />
            </ListItem>
            {/* Recharge */}
            <ListItem
              sx={{
                backgroundColor: "#fff",
                borderRadius: "8px",
                marginBottom: "10px",
                boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
              }}
              button
              onClick={()=>setIsRecharge(true)}
            >
              <ListItemText primary="Recharge" />
              <ArrowForwardIosIcon />
            </ListItem>
          </List>
        </Box>
      )}
    </Box>
  );
};

export default RequestComponent;
